import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';

/** Inactivity window before automatic logout (MH round-2 item 2: ~10 minutes). */
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;

// Events that count as user activity. Deliberately cheap, passive listeners.
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'mousemove', 'wheel', 'touchstart'] as const;

/**
 * Logs the user out after 10 minutes of inactivity.
 *
 * Active users are never interrupted: any interaction resets the timer, and
 * the token-refresh interceptor keeps their session alive indefinitely while
 * they work. This hook only catches walked-away sessions.
 */
export function useIdleLogout(): void {
  const { isAuthenticated, logout } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fire = () => {
      void logout().finally(() => {
        window.location.href = '/login?reason=idle';
      });
    };

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fire, IDLE_TIMEOUT_MS);
    };

    reset();
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, reset, { passive: true });
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, reset);
      }
    };
  }, [isAuthenticated, logout]);
}
